/**
 * React Query hooks for LPA Tags.
 *
 * Tags are tenant-scoped (no factory_id).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/core/lib/queryKeys';
import { useTenant } from '@/core/hooks/useTenant';
import { useToast } from '@/shared/ui/use-toast';
import { useLanguage } from '@/core/LanguageContext';
import {
  fetchTags,
  createTag,
  updateTag,
  deleteTag,
} from '../api/tags';
import type { CreateTagInput, UpdateTagInput } from '../types';

// ---------------------------------------------------------------------------
// Query
// ---------------------------------------------------------------------------

export function useTags() {
  const { selectedTenantId } = useTenant();

  return useQuery({
    queryKey: queryKeys.modLpaTags(selectedTenantId),
    queryFn: () => fetchTags(selectedTenantId!),
    enabled: !!selectedTenantId,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateTag() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (input: Omit<CreateTagInput, 'tenant_id'>) =>
      createTag({ ...input, tenant_id: selectedTenantId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaTags(selectedTenantId),
      });
      toast({
        title: t('mod_lpa.tags.createSuccess', 'Tag created'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.tags.createError', 'Failed to create tag'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateTag() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ tagId, input }: { tagId: string; input: UpdateTagInput }) =>
      updateTag(tagId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaTags(selectedTenantId),
      });
      // Also invalidate questions since they embed tag data
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === 'mod_lpa_questions',
      });
      toast({
        title: t('mod_lpa.tags.updateSuccess', 'Tag updated'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.tags.updateError', 'Failed to update tag'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteTag() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (tagId: string) => deleteTag(tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaTags(selectedTenantId),
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === 'mod_lpa_questions',
      });
      toast({
        title: t('mod_lpa.tags.deleteSuccess', 'Tag deleted'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.tags.deleteError', 'Failed to delete tag'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
